import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { sanitize } from '../lib/sanitize';
import SeoHead from '../components/seo/SeoHead';
import styles from '../styles/ProductPage.module.css';
import { useDispatch } from 'react-redux';
import { addToCart } from '../store/cartSlice';
import toast from 'react-hot-toast';
import { useStoreSettings } from '../hooks/useStoreSettings';
import ProductDetails from '../components/products/ProductDetails';
import ProductDescription from '../components/products/ProductDescription';
import CountdownTimer from '../components/CountdownTimer';
import WriteReviewLink from '../components/reviews/WriteReviewLink';
import ProductReviews from '../components/reviews/ProductReviews';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  old_price?: number;
  discount_percentage?: number;
  image_url: string;
  category: string;
  stock_quantity: number;
  slug: string;
  vendor: string;
  show_vendor?: boolean;
  sku: string;
  variants: {
    size: string;
    color: string;
    stock: number;
  }[];
  gallery_images: string[];
  rating: number;
  reviews_count: number;
  related_products: string[];
  additional_information: string;
  shipping_returns: string;
  show_additional_information?: boolean;
  show_shipping_returns?: boolean;
  short_description: string;
  module_ids?: string[];
}

interface ProductOption {
  id: string;
  product_id: string;
  name: string;
  values: ProductOptionValue[];
}

interface ProductOptionValue {
  id: string;
  option_id: string;
  value: string;
  is_active?: boolean;
}

interface RelatedProduct {
  id: string;
  name: string;
  price: number;
  old_price?: number;
  image_url: string;
  slug: string;
}

interface Module {
  id: string;
  name: string;
  description: string;
  type: string;
  content: any;
  location: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface BundleProduct {
  id: string;
  product_id: string;
  quantity: number;
  is_gift: boolean;
  products: {
    id: string;
    name: string;
    price: number;
    image_url: string;
    sku: string;
  }[];
}

// Interfejs koji odgovara strukturi podataka koju vraća Supabase
interface ProductData {
  id: string;
  name: string;
  price: number;
  image_url: string;
  sku: string;
  normalizedPrice: number; // Obavezno polje
}

interface BundleProductItem {
  id: string;
  product_id: string;
  quantity: number;
  is_gift: boolean;
  products: ProductData;
  bundle?: any;
}

// Interfejs za sirove podatke iz baze
interface RawBundleProductItem {
  id: string;
  product_id: string;
  quantity: number;
  is_gift: boolean;
  products: unknown; // može biti bilo šta, posle ćemo proveriti i konvertovati
}

// Kreiranje interfejsa za podatke o originalnom bundlu za bolju tipizaciju
interface OriginalBundle {
  id: string;
  name: string;
  description: string;
  bundle_type: string;
  discount_type: string;
  discount_value: number;
  price?: number;
  is_active: boolean;
}

const ProductPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedImage, setSelectedImage] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState<RelatedProduct[]>([]);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [productModules, setProductModules] = useState<Module[]>([]);
  const [bundleProducts, setBundleProducts] = useState<Record<string, BundleProductItem[]>>({});
  const [loadingBundleProducts, setLoadingBundleProducts] = useState<Record<string, boolean>>({});
  // Dostava za JSON-LD shippingDetails (domaća stopa + free-shipping prag).
  const [shipping, setShipping] = useState<{ rate: number | null; freeThreshold: number | null; freeEnabled: boolean }>({
    rate: null,
    freeThreshold: null,
    freeEnabled: false,
  });
  const dispatch = useDispatch();
  const { formatPrice, settings: storeSettings } = useStoreSettings();
  const { defaultCurrency } = useStoreSettings();
  const headerLayout = (storeSettings as any)?.product_header_layout || {
    price: 1,
    description: 2,
    rating: 3,
  };
  const layoutOrders = {
    desc: headerLayout.description,
    rating: headerLayout.rating,
    price: headerLayout.price,
  };

  // Funkcija za izračunavanje cena za bundle modul
  const calculateBundlePrices = (module: Module) => {
    // Podrazumevane vrednosti ako nemamo bundle proizvode
    let originalPrice = 0;
    let currentPrice = 0;
    
    // Provera da li imamo proizvode u bundle-u i da li je modul aktiviran
    if (
      module.id && 
      bundleProducts[module.id] && 
      Array.isArray(bundleProducts[module.id]) && 
      bundleProducts[module.id].length > 0
    ) {
      // Računamo originalnu cenu kao zbir originalnih cena svih proizvoda u bundle-u
      originalPrice = bundleProducts[module.id].reduce((sum: number, item: BundleProductItem) => {
        return sum + (item.products.price || 0) * (item.quantity || 1);
      }, 0);
      
      // Računamo trenutnu cenu kao zbir normalizovanih cena svih proizvoda u bundle-u
      currentPrice = bundleProducts[module.id].reduce((sum: number, item: BundleProductItem) => {
        return sum + (item.products.normalizedPrice || 0) * (item.quantity || 1);
      }, 0);
    } 
    // Ako nema proizvoda, ali postoji fiksna cena u bundle-u
    else if (module.content?.bundle_price) {
      currentPrice = module.content.bundle_price;
      originalPrice = module.content.original_price || currentPrice;
    }
    
    // Izračunavanje uštede
    const savings = originalPrice - currentPrice;
    
    return { originalPrice, currentPrice, savings };
  };

  useEffect(() => {
    const fetchProduct = async () => {
      if (!slug) return;
      
      try {
        // Dohvati proizvod i proveri da li pripada vidljivoj kategoriji
        const { data, error } = await supabase
          .from('products')
          .select(`
            *,
            categories!inner(id, name, is_visible)
          `)
          .eq('slug', slug)
          .eq('categories.is_visible', true)
          .single();

        if (error) throw error;
        console.log('Fetched product:', data);
        setProduct(data);
        
        // Fetch product options after product is fetched
        if (data && data.id) {
          fetchProductOptions(data.id);
          
          // Dohvati slične proizvode iz iste kategorije
          fetchRelatedProducts(data.category_id, data.id);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        setError('Product not found or is not available');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]);

  // Dohvat podataka o dostavi za JSON-LD (RLS sam scope-uje na tenant).
  useEffect(() => {
    if (!product?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [{ data: freeCfg }, { data: rateRows }] = await Promise.all([
          supabase.from('free_shipping_config').select('threshold, enabled').maybeSingle(),
          supabase
            .from('product_shipping_rates')
            .select('shipping_rates!inner(rate)')
            .eq('product_id', product.id),
        ]);
        if (cancelled) return;
        // Domaća stopa = najviša pozitivna stopa vezana za proizvod (stvarna
        // naplata dostave); 0/free unosi se ignorišu ovde, prag ih pokriva.
        const rates = (rateRows || [])
          .map((r: any) => Number(r.shipping_rates?.rate))
          .filter((n) => Number.isFinite(n) && n > 0);
        setShipping({
          rate: rates.length ? Math.max(...rates) : null,
          freeThreshold: freeCfg?.enabled ? Number(freeCfg.threshold) : null,
          freeEnabled: Boolean(freeCfg?.enabled),
        });
      } catch (e) {
        console.error('Error fetching shipping for JSON-LD:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [product?.id]);

  const fetchProductOptions = async (productId: string) => {
    try {
      const { data: optionsData, error } = await supabase
        .from('product_options')
        .select('*, values:product_option_values(*)')
        .eq('product_id', productId);
      if (error) throw error;

      const optionsWithValues = (optionsData || []).map((option: any) => ({
        ...option,
        values: (option.values || []).filter((v: any) => v.is_active !== false),
      }));

      setProductOptions(optionsWithValues);
      
      // Initialize selectedOptions with first value of each option
      const initialSelectedOptions: Record<string, string> = {};
      optionsWithValues.forEach((option: ProductOption) => {
        if (option.values && option.values.length > 0) {
          initialSelectedOptions[option.id] = option.values[0].value;
        }
      });
      
      setSelectedOptions(initialSelectedOptions);
      
    } catch (error) {
      console.error('Error fetching product options:', error);
    }
  };
  
  const handleOptionChange = (optionId: string, value: string) => {
    setSelectedOptions({
      ...selectedOptions,
      [optionId]: value
    });
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    // Prepare selected options for cart
    const selectedOptionsList = Object.entries(selectedOptions).map(([optionId, value]) => {
      const option = productOptions.find(opt => opt.id === optionId);
      return {
        name: option?.name || '',
        value
      };
    });
    
    dispatch(addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url,
      quantity: quantity,
      selectedOptions: selectedOptionsList
    }));

    toast.success('Added to cart!');
  };

  useEffect(() => {
    if (product?.module_ids && product.module_ids.length > 0) {
      fetchProductModules(product.module_ids);
    }
  }, [product?.module_ids]);

  const fetchProductModules = async (moduleIds: string[]) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('modules')
        .select('*')
        .in('id', moduleIds)
        .eq('is_active', true);

      if (error) throw error;
      
      console.log("Moduli iz baze:", data);
      
      if (data) {
        setProductModules(data);
        
        // Inicijalno učitavamo informacije o proizvodima za bundle module
        for (const module of data) {
          if (module.type === 'bundle' && module.content.bundle_id) {
            console.log(`Dohvatam proizvode za bundle ${module.content.bundle_id} u modulu ${module.id}`);
            await fetchBundleProducts(module.content.bundle_id, module.id);
          }
        }
      }
    } catch (error) {
      console.error("Greška pri učitavanju modula:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchBundleProducts = async (bundleId: string, moduleId: string) => {
    try {
      setLoadingBundleProducts(prev => ({ ...prev, [moduleId]: true }));
      
      // Prvo dohvatamo podatke o bundlu da bismo imali tačne informacije o popustima
      const { data: bundleData, error: bundleError } = await supabase
        .from('bundles')
        .select('*')
        .eq('id', bundleId)
        .single();
        
      if (bundleError) {
        console.error("Error fetching bundle data:", bundleError);
        throw bundleError;
      }
      
      console.log("Original bundle data:", bundleData);
      
      const { data, error } = await supabase
        .from('bundle_product_items')
        .select(`
          id,
          product_id,
          quantity,
          is_gift,
          sort_order,
          products (
            id,
            name,
            price,
            old_price,
            image_url,
            sku
          )
        `)
        .eq('bundle_id', bundleId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      
      // Debugging: Ispisujemo stvarnu strukturu podataka koju dobijamo iz Supabase
      console.log("Bundle products raw data:", JSON.stringify(data, null, 2));
      
      // Provera nedostajućih slika proizvoda
      const missingImages = data.filter(item => {
        const prod = Array.isArray(item.products) ? item.products[0] : item.products;
        return !prod || !prod.image_url;
      }).map(item => ({
        id: item.id,
        product_id: item.product_id,
        product_data: item.products
      }));
      
      if (missingImages.length > 0) {
        console.warn("Proizvodi bez slika u bundle-u:", missingImages);
      }
      
      if (data && data.length > 0) {
        // Konvertujemo podatke iz Supabase u naše tipove
        const processedData: BundleProductItem[] = data.map(item => {
          // Provera da li je products niz ili objekat i pravilna konverzija
          let productData: ProductData;
          
          if (Array.isArray(item.products)) {
            // Ako je niz, uzimamo prvi element
            productData = item.products[0] as unknown as ProductData;
          } else {
            // Inače, tretiramo kao objekat
            productData = item.products as unknown as ProductData;
          }
          
          // Odredimo originalnu cenu proizvoda (stara cena pre popusta)
          const originalPrice = productData.price;
          
          // Izračunavamo sniženu cenu na osnovu tipa popusta u bundlu
          let discountedPrice = originalPrice;
          
          if (bundleData) {
            // Primenimo popust na osnovu tipa iz bundla
            if (bundleData.discount_type === 'PERCENTAGE' && bundleData.discount_value > 0) {
              // Procentualni popust
              discountedPrice = originalPrice * (1 - bundleData.discount_value / 100);
            } else if (bundleData.bundle_type === 'FIXED_PRICE_BUNDLE' && bundleData.price !== undefined) {
              // Ako je fiksna cena za ceo bundle, moramo proporcionalno izračunati
              // Prvo izračunamo ukupnu originalnu cenu svih proizvoda
              const totalOriginalPrice = data.reduce((sum, prodItem: RawBundleProductItem) => {
                const prodPrice = Array.isArray(prodItem.products) 
                  ? (prodItem.products as any)[0].price 
                  : (prodItem.products as any).price;
                return sum + (prodPrice * prodItem.quantity);
              }, 0);
              
              // Zatim izračunamo proporciju ovog proizvoda
              const proportion = originalPrice / totalOriginalPrice;
              // Primenimo proporciju na fiksnu cenu bundla
              discountedPrice = bundleData.price * proportion;
            } else if (bundleData.discount_type === 'FIXED_AMOUNT' && bundleData.discount_value > 0) {
              // Fiksni popust proporcionalno raspodeljen na proizvode
              // Prvo izračunamo ukupnu originalnu cenu svih proizvoda
              const totalOriginalPrice = data.reduce((sum, prodItem: RawBundleProductItem) => {
                const prodPrice = Array.isArray(prodItem.products) 
                  ? (prodItem.products as any)[0].price 
                  : (prodItem.products as any).price;
                return sum + (prodPrice * prodItem.quantity);
              }, 0);
              
              // Zatim izračunamo proporciju ovog proizvoda
              const proportion = originalPrice / totalOriginalPrice;
              // Primenimo proporciju na fiksni popust
              const discountAmount = bundleData.discount_value * proportion;
              discountedPrice = originalPrice - discountAmount;
            }
          }
          
          // Osiguramo da cena nije negativna ili 0
          discountedPrice = Math.max(discountedPrice, 0.01);
          
          console.log(`Proizvod ${productData.name}: originalna cena = ${originalPrice}, snižena cena = ${discountedPrice}`);
          
          return {
            id: item.id,
            product_id: item.product_id,
            quantity: item.quantity,
            is_gift: item.is_gift,
            bundle: bundleData as OriginalBundle,
            products: {
              ...productData,
              // Postavimo normalizedPrice na izračunatu sniženu cenu
              normalizedPrice: discountedPrice
            }
          };
        });
        
        // Ažuriramo state sa podacima o proizvodima u bundle-u
        setBundleProducts(prev => ({ ...prev, [moduleId]: processedData }));
      }
    } catch (error) {
      console.error(`Error fetching products for bundle ${bundleId}:`, error);
    } finally {
      setLoadingBundleProducts(prev => ({ ...prev, [moduleId]: false }));
    }
  };

  const fetchProductReviews = async (productId: string) => {
    try {
      // Proverimo da li postoje vrednosti u proizvodu
      if (product && (product.reviews_count !== undefined || product.rating !== undefined)) {
        console.log('Koriste se postojeće vrednosti iz proizvoda:', { 
          count: product.reviews_count, 
          rating: product.rating 
        });
        return;
      }
      
      // Ako vrednosti ne postoje u proizvodu, dohvatamo ih preko funkcija
      console.log('Dohvatanje vrednosti preko funkcija');
      
      // Dohvatamo broj recenzija
      const { data: reviewsCountData, error: reviewsCountError } = await supabase
        .rpc('get_product_reviews_count', { product_uuid: productId });
        
      if (reviewsCountError) throw reviewsCountError;
      
      // Dohvatamo prosečnu ocenu
      const { data: avgRatingData, error: avgRatingError } = await supabase
        .rpc('get_product_average_rating', { product_uuid: productId });
        
      if (avgRatingError) throw avgRatingError;
      
      console.log('Recenzije (preko funkcija):', { count: reviewsCountData, rating: avgRatingData });
      
      // Ažuriramo proizvod sa novim podacima o recenzijama
      if (product) {
        setProduct({
          ...product,
          reviews_count: reviewsCountData || 0,
          rating: avgRatingData || 0
        });
      }
    } catch (error) {
      console.error('Error fetching reviews data:', error);
    }
  };
  
  const handleReviewSubmitted = () => {
    if (product?.id) {
      fetchProductReviews(product.id);
    }
  };
  
  useEffect(() => {
    if (product?.id) {
      fetchProductReviews(product.id);
    }
  }, [product?.id]);

  // ModuleDisplay component to render different types of modules
  const ModuleDisplay = ({ module }: { module: Module }) => {
    const renderModuleContent = () => {
      switch (module.type) {
        case 'text':
          return (
            <div
              className={styles.textModule}
              dangerouslySetInnerHTML={{ __html: sanitize(module.content.text || '') }}
            />
          );
        case 'html':
          return <div className={styles.moduleContent} dangerouslySetInnerHTML={{ __html: sanitize(module.content.html) }} />;
        case 'image':
          return (
            <div className={styles.imageModule}>
              <a href={module.content.link || '#'} className={module.content.link ? styles.linkedImage : ''}>
                <img 
                  src={module.content.url} 
                  alt={module.content.alt || module.name} 
                  className={styles.moduleImage}
                />
              </a>
            </div>
          );
        case 'banner':
          return (
            <div className={styles.bannerModule}>
              <div className={styles.bannerContent}>
                {module.content.heading && <h3>{module.content.heading}</h3>}
                {module.content.subheading && <p>{module.content.subheading}</p>}
                {module.content.button_text && module.content.button_link && (
                  <a href={module.content.button_link} className={styles.bannerButton}>
                    {module.content.button_text}
                  </a>
                )}
              </div>
              {module.content.image_url && (
                <img 
                  src={module.content.image_url} 
                  alt={module.content.heading || module.name} 
                  className={styles.bannerImage}
                />
              )}
            </div>
          );
        case 'cta':
          return (
            <div 
              className={styles.ctaModule}
              style={{ backgroundColor: module.content.background_color || '#f8f9fa' }}
            >
              {module.content.heading && <h3>{module.content.heading}</h3>}
              {module.content.text && <p>{module.content.text}</p>}
              {module.content.button_text && module.content.button_link && (
                <a href={module.content.button_link} className={styles.ctaButton}>
                  {module.content.button_text}
                </a>
              )}
            </div>
          );
        case 'gallery':
          return (
            <div className={styles.galleryModule}>
              <div className={styles.galleryGrid}>
                {module.content.images && module.content.images.map((imgUrl: string, index: number) => (
                  <div key={index} className={styles.galleryImage}>
                    <img src={imgUrl} alt={`Gallery image ${index + 1}`} width={300} height={300} loading="lazy" decoding="async" />
                  </div>
                ))}
              </div>
            </div>
          );
        case 'countdown':
          return (
            <div 
              className={styles.countdownModule}
              style={{ 
                backgroundColor: module.content.background_color || '#1a1a1a',
                color: module.content.text_color || '#ffffff'
              }}
            >
              <CountdownTimer 
                endDate={module.content.end_date} 
                heading={module.content.heading}
                subheading={module.content.subheading}
                buttonText={module.content.button_text}
                buttonLink={module.content.button_link}
              />
            </div>
          );
        case 'bundle': {
          if (!module.content.bundle_id) {
            return <div>Bundle is not selected</div>;
          }
          
          // Računamo originalnu cenu, trenutnu cenu i uštedu
          const { originalPrice, currentPrice, savings } = calculateBundlePrices(module);
          
          return (
            <div id={`module-${module.id}`} className={styles.moduleContent}>
              <div className={styles.bundleContainer}>
                {module.content.heading && (
                  <h3 className={styles.bundleHeading}>{module.content.heading}</h3>
                )}
                
                {loadingBundleProducts[module.id] ? (
                  <p>Učitavanje proizvoda...</p>
                ) : bundleProducts[module.id] && bundleProducts[module.id].length > 0 ? (
                  <div className={styles.bundleHorizontal}>
                    {bundleProducts[module.id].map((item: BundleProductItem, index: number) => (
                      <React.Fragment key={item.id}>
                        <div className={styles.bundleProductCard}>
                          <div className={styles.bundleProductImageHorizontal}>
                            {item.products && item.products.image_url ? (
                              <img 
                                src={item.products.image_url} 
                                alt={item.products.name || `Proizvod ${index + 1}`}
                                onError={(e) => {
                                  console.error(`Error loading image for product ${item.product_id}:`, item.products.image_url);
                                  const target = e.target as HTMLImageElement;
                                  target.onerror = null; // Sprečava beskonačne pokušaje
                                  target.src = '/logo192.png'; // Postavi default sliku
                                }}
                              />
                            ) : (
                              <div style={{ width: '100%', height: '100%', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span>No image</span>
                              </div>
                            )}
                          </div>
                          <div className={styles.bundleProductInfoHorizontal}>
                            <p className={styles.bundleStrikePrice}>
                              {/* Prikazuje originalnu cenu (staru cenu) */}
                              {item.products.price.toFixed(2)} RSD
                            </p>
                            <p className={styles.bundleProductPriceHorizontal}>
                              {/* Prikazuje normalizedPrice (sniženu cenu) */}
                              {item.products.normalizedPrice.toFixed(2)} RSD
                            </p>
                            {item.is_gift && <span className={styles.giftBadge}>POKLON</span>}
                          </div>
                        </div>
                        
                        {index < bundleProducts[module.id].length - 1 && (
                          <div className={styles.bundlePlusSign}>+</div>
                        )}
                      </React.Fragment>
                    ))}
                    
                    <div className={styles.bundlePlusSign}>=</div>
                    
                    <div className={styles.bundleSummary}>
                      {originalPrice > currentPrice && (
                        <p className={styles.bundleOriginalPrice}>
                          {/* Eksplicitno koristim izračunatu originalnu cenu */}
                          {originalPrice.toFixed(2)} RSD
                        </p>
                      )}
                      <p className={styles.bundleFinalPrice}>
                        {/* Eksplicitno koristim izračunatu sniženu cenu */}
                        {currentPrice.toFixed(2)} RSD
                      </p>
                      
                      {savings > 0 && (
                        <p className={styles.bundleSavings}>
                          {/* Eksplicitno koristim izračunatu uštedu */}
                          Save {savings.toFixed(2)} RSD!
                        </p>
                      )}
                      
                      <button 
                        className={styles.bundleAddToCartButton}
                        onClick={() => {
                          // Debug what's in the bundleProductsList
                          console.log('Bundle products to add:', JSON.stringify(bundleProducts[module.id], null, 2));
                          
                          if (!bundleProducts[module.id] || bundleProducts[module.id].length === 0) {
                            toast.error('Nema proizvoda u ovom paketu!');
                            return;
                          }
                          
                          // Check if the object structure is as expected
                          if (!Array.isArray(bundleProducts[module.id])) {
                            console.error('bundleProductsList is not an array:', bundleProducts[module.id]);
                            toast.error('Greška prilikom dodavanja paketa u korpu!');
                            return;
                          }
                          
                          // Nalazimo sliku prvog proizvoda za animaciju
                          let firstProductImage = "/logo192.png";
                          if (bundleProducts[module.id][0] && bundleProducts[module.id][0].products && bundleProducts[module.id][0].products.image_url) {
                            firstProductImage = bundleProducts[module.id][0].products.image_url;
                          }
                          
                          console.log('Struktura bundleProductsList pre dodavanja u korpu:', 
                            bundleProducts[module.id].map(item => ({
                              id: item.id,
                              product_id: item.product_id,
                              quantity: item.quantity,
                              is_gift: item.is_gift,
                              product_name: item.products?.name,
                              has_image: !!item.products?.image_url
                            }))
                          );
                          
                          // Create a copy to avoid mutation issues
                          const productsToAdd = [...bundleProducts[module.id]];
                          
                          // Generišemo jedinstveni ID za ovaj bundle
                          const bundleInstanceId = `module-bundle-${module.id}-${Date.now()}`;
                          
                          // Dodajemo proizvode u korpu koristeći već izračunate cene
                          productsToAdd.forEach((item, index) => {
                            // Dodatna provera da item i item.products postoje
                            if (!item || !item.products) {
                              console.error(`Invalid bundle product at index ${index}:`, item);
                              return; // Skip this item
                            }
                            
                            // Koristimo već izračunatu normalizedPrice (sniženu cenu)
                            const itemPrice = item.products.normalizedPrice;
                            
                            console.log(`Dodajem u korpu ${item.products.name}: Cena: ${itemPrice}, Quantity: ${item.quantity}`);
                            
                            // Ako imamo rate faktor u valuti, moramo osigurati da cena bude tačna
                            // rate se koristi pri formatiranju cene, pa delimo sa njim da dobijemo osnovnu vrednost
                            let rateAdjustedPrice = itemPrice;
                            if (defaultCurrency?.rate && defaultCurrency.rate !== 1) {
                              rateAdjustedPrice = itemPrice / defaultCurrency.rate;
                              console.log(`Currency rate: ${defaultCurrency.rate}, Original price: ${itemPrice}, Rate adjusted price: ${rateAdjustedPrice}`);
                            }
                            
                            // Direktno dodavanje u localStorage za debugging
                            try {
                              // Proveravamo da li product ima sliku
                              if (!item.products.image_url) {
                                console.warn(`Proizvod ${item.products.name} nema sliku! Postavljam default sliku.`);
                                // Ako nema slike, postavićemo default sliku
                                item.products.image_url = '/logo192.png';
                              }
                              
                              // Dodajemo timestamp za jedinstveni ID
                              const uniqueId = `${item.products.id}-${Date.now()}-${index}`;
                              const bundleId = item.bundle?.id || module.id; // Koristimo bundle.id ili module.id kao bundleId
                              
                              const cartItem = {
                                id: item.products.id, // Izvorno ostavimo originalni ID, cartSlice će generisati jedinstveni ID
                                name: item.products.name,
                                price: rateAdjustedPrice,
                                image_url: item.products.image_url,
                                quantity: item.quantity || 1,
                                fromBundle: true,
                                bundleId: bundleId, // Dodajemo ID bundle-a
                                bundleInstanceId: bundleInstanceId
                              };
                              
                              console.log(`Adding to cart: ${JSON.stringify(cartItem)}`);
                              
                              // Dodaj proizvod u korpu
                              dispatch(addToCart(cartItem));
                              
                              console.log(`Cart item ${index + 1}/${productsToAdd.length} added successfully`);
                            } catch (error) {
                              console.error(`Error adding item ${index} to cart:`, error);
                            }
                          });
                          
                          // Emitujemo događaj za animaciju proizvoda
                          const event = new CustomEvent('product-added-to-cart', {
                            detail: {
                              productName: module.content.bundle_type || 'Bundle',
                              quantity: productsToAdd.length,
                              productImageUrl: firstProductImage,
                              productElement: `#module-${module.id}` // ID selektora za module
                            }
                          });
                          window.dispatchEvent(event);
                          
                          console.log(`Added ${productsToAdd.length} items to cart`);
                          toast.success(`Bundle je dodat u korpu! (${productsToAdd.length} proizvoda)`);
                        }}
                      >
                        Dodaj u korpu
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className={styles.bundleType}>{module.content.bundle_type}</p>
                )}
              </div>
            </div>
          );
        }
        default:
          return null;
      }
    };

    return (
      <div className={styles.moduleWrapper}>
        {renderModuleContent()}
      </div>
    );
  };

  // Funkcija za dohvatanje sličnih proizvoda iz iste kategorije
  const fetchRelatedProducts = async (categoryId: string, currentProductId: string) => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('category_id', categoryId)
        .neq('id', currentProductId) // Isključujemo trenutni proizvod
        .limit(4); // Ograničavamo broj sličnih proizvoda

      if (error) throw error;
      console.log('Fetched related products:', data);
      setRelatedProducts(data || []);
    } catch (error) {
      console.error('Error fetching related products:', error);
    }
  };

  // Rezervǐsi visinu dok se učitava: bez ovoga footer renderuje pri vrhu pa
  // padne kad stigne sadržaj (glavni izvor CLS-a na product strani).
  if (loading) return <div style={{ minHeight: '100vh', padding: 40, textAlign: 'center' }}>Učitavanje...</div>;
  if (error) return <div style={{ minHeight: '100vh', padding: 40, textAlign: 'center' }}>Greška: {error}</div>;
  if (!product) return <div style={{ minHeight: '100vh', padding: 40, textAlign: 'center' }}>Proizvod nije pronađen</div>;

  // priceValidUntil: jednu godinu unapred (uklanja Search Console upozorenje).
  const priceValidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const ldCurrency = defaultCurrency?.code || 'RSD';
  const ldSellerName = storeSettings?.store_name?.trim();

  // Efektivna cena dostave za ovaj proizvod: 0 ako cena dostiže free-shipping
  // prag, inače domaća stopa. null = nemamo pouzdan podatak → izostavi shipping.
  const ldShippingCost =
    shipping.freeEnabled && shipping.freeThreshold != null && product && product.price >= shipping.freeThreshold
      ? 0
      : shipping.rate;

  const productLd = product
    ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: (product.short_description || product.description || '').replace(/<[^>]*>/g, '').slice(0, 500),
        // Apsolutni URL-ovi + dedup: Google ignoriše relativne slike u JSON-LD.
        image: Array.from(
          new Set([product.image_url, ...(product.gallery_images || [])].filter(Boolean))
        ).map((u) =>
          /^https?:\/\//i.test(u) || typeof window === 'undefined'
            ? u
            : window.location.origin + (u.startsWith('/') ? u : '/' + u)
        ),
        sku: product.sku,
        brand: product.vendor ? { '@type': 'Brand', name: product.vendor } : undefined,
        category: (product as any).categories?.name,
        // Samo kad stvarno ima recenzija — lažan rating je rizik za Google penal.
        ...(product.reviews_count > 0 && product.rating > 0
          ? {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: product.rating,
                reviewCount: product.reviews_count,
                bestRating: 5,
                worstRating: 1,
              },
            }
          : {}),
        offers: {
          '@type': 'Offer',
          priceCurrency: ldCurrency,
          price: product.price,
          priceValidUntil,
          itemCondition: 'https://schema.org/NewCondition',
          availability:
            (product.stock_quantity ?? 0) > 0
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          ...(ldSellerName
            ? { seller: { '@type': 'Organization', name: ldSellerName } }
            : {}),
          // 14-dnevni rok je zakonska obaveza u Srbiji (Zakon o zaštiti potrošača).
          hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy',
            applicableCountry: 'RS',
            returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
            merchantReturnDays: 14,
            returnMethod: 'https://schema.org/ReturnByMail',
          },
          // Stvarni trošak dostave po tenantu; izostavi ako nema pouzdanog podatka.
          ...(ldShippingCost != null
            ? {
                shippingDetails: {
                  '@type': 'OfferShippingDetails',
                  shippingRate: {
                    '@type': 'MonetaryAmount',
                    value: ldShippingCost,
                    currency: ldCurrency,
                  },
                  shippingDestination: {
                    '@type': 'DefinedRegion',
                    addressCountry: 'RS',
                  },
                  deliveryTime: {
                    '@type': 'ShippingDeliveryTime',
                    handlingTime: {
                      '@type': 'QuantitativeValue',
                      minValue: 0,
                      maxValue: 1,
                      unitCode: 'DAY',
                    },
                    transitTime: {
                      '@type': 'QuantitativeValue',
                      minValue: 1,
                      maxValue: 3,
                      unitCode: 'DAY',
                    },
                  },
                },
              }
            : {}),
        },
      }
    : null;

  const breadcrumbLd = product
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Početna', item: typeof window !== 'undefined' ? window.location.origin + '/' : undefined },
          (product as any).categories?.name && {
            '@type': 'ListItem',
            position: 2,
            name: (product as any).categories.name,
            item:
              typeof window !== 'undefined' && (product as any).categories?.id
                ? `${window.location.origin}/category/${(product as any).categories.id}`
                : undefined,
          },
          { '@type': 'ListItem', position: 3, name: product.name },
        ].filter(Boolean),
      }
    : null;

  return (
    <div className={styles.productPage}>
      {product && (
        <SeoHead
          title={product.name}
          titleOverride={(product as any).seo_title}
          description={(product as any).seo_description || product.short_description || product.description || undefined}
          image={(product as any).seo_image_url || product.image_url || undefined}
          type="product"
          noindex={Boolean((product as any).noindex)}
          jsonLd={[productLd, breadcrumbLd].filter(Boolean) as object[]}
        />
      )}
      <nav className={styles.breadcrumbs}>
        <Link to="/">Početna</Link>
        <span className={styles.separator}>/</span>
        <Link to="/collections">Kolekcije</Link>
        <span className={styles.separator}>/</span>
        <span className={styles.current}>{product?.name}</span>
      </nav>

      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.productDetails}>
            <div className={styles.productGallery}>
              <img
                src={selectedImage === 0 ? product.image_url : product.gallery_images[selectedImage - 1]}
                alt={product.name}
                className={styles.mainImage}
                width={600}
                height={600}
                fetchPriority="high"
                decoding="async"
              />
              
              <div className={styles.thumbnailGallery}>
                <button
                  className={`${styles.thumbnail} ${selectedImage === 0 ? styles.active : ''}`}
                  onClick={() => setSelectedImage(0)}
                >
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className={styles.thumbnailImage}
                    width={80}
                    height={80}
                    loading="lazy"
                    decoding="async"
                  />
                </button>

                {product.gallery_images.map((img, index) => (
                  <button
                    key={index}
                    className={`${styles.thumbnail} ${selectedImage === index + 1 ? styles.active : ''}`}
                    onClick={() => setSelectedImage(index + 1)}
                  >
                    <img
                      src={img}
                      alt={`${product.name} ${index + 1}`}
                      className={styles.thumbnailImage}
                      width={80}
                      height={80}
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.productInfo}>
              <h1>{product.name}</h1>
              
              <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div className={styles.priceContainer} style={{ order: layoutOrders.price }}>
                {product.old_price && (
                  <span className={styles.oldPrice}>
                    {formatPrice(product.old_price)}
                  </span>
                )}
                <div className={styles.priceRow}>
                  <span className={styles.currentPrice}>
                    {formatPrice(product.price)}
                  </span>
                  {product.old_price && (
                    <span className={styles.discount}>
                      {Math.round(((product.old_price - product.price) / product.old_price) * 100)}% Popust
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.shortDescription} style={{ order: layoutOrders.desc }}>
                <span
                  dangerouslySetInnerHTML={{ __html: sanitize(product.short_description) }}
                />
                <a 
                  href="#description" 
                  className={styles.readMore}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('description');
                    document.querySelector('#description')?.scrollIntoView({ 
                      behavior: 'smooth' 
                    });
                  }}
                >
                  Više...
                </a>
              </div>

              <div className={styles.productRating} style={{ order: layoutOrders.rating }}>
                <div className={styles.stars}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <span
                      key={star}
                      className={star <= Math.round(product.rating || 0) ? styles.filled : ''}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <div className={styles.ratingContainer}>
                  <div className={styles.ratingText}>
                    <strong>{product.rating ? product.rating.toFixed(1) : '0.0'}</strong> ({product.reviews_count || 0} {product.reviews_count === 1 ? 'recenzija' : product.reviews_count >= 5 ? 'recenzija' : 'recenzije'})
                  </div>
                  <WriteReviewLink
                    productId={product.id}
                    onReviewSubmitted={handleReviewSubmitted}
                  />
                </div>
              </div>
              </div>

              {/* Modules + attributes order is controlled by the per-product
                  attributes_above_modules flag via flex `order` on this wrapper. */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
              {productModules.length > 0 ? (
                <div
                  className={styles.productModules}
                  style={{ order: (product as any).attributes_above_modules ? 2 : 1 }}
                >
                  {productModules.map(module => (
                    <ModuleDisplay key={module.id} module={module} />
                  ))}
                </div>
              ) : (
                <div
                  className={styles.stockInfo}
                  style={{ order: (product as any).attributes_above_modules ? 2 : 1 }}
                >
                  {product.stock_quantity > 0 ? (
                    <span>Požurite! Samo {product.stock_quantity} komada na stanju</span>
                  ) : (
                    <span>Nema na stanju</span>
                  )}
                </div>
              )}

              <div
                className={styles.productOptionsLayout}
                style={{ order: (product as any).attributes_above_modules ? 1 : 2 }}
              >
                <div className={styles.leftColumn}>
                  {/* Product Options Section */}
                  {productOptions.length > 0 && (
                    <div className={styles.productOptions}>
                      {productOptions.map((option) => (
                        <div key={option.id} className={styles.optionSelector}>
                          <label className={styles.optionLabel}>{option.name}:</label>
                          <div className={styles.optionValues}>
                            {option.values
                              .filter(value => value.is_active !== false)
                              .map((value) => (
                              <button
                                key={value.id}
                                className={`${styles.optionValue} ${selectedOptions[option.id] === value.value ? styles.selected : ''}`}
                                onClick={() => handleOptionChange(option.id, value.value)}
                              >
                                {value.value}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {product.variants && product.variants.length > 0 && (
                    <div className={styles.variantSelectors}>
                      <div className={styles.sizeSelector}>
                        <label>Veličina:</label>
                        <div className={styles.options}>
                          {[...new Set(product.variants.map(v => v.size))].map(size => (
                            <button 
                              key={size}
                              className={`${styles.sizeOption} ${selectedSize === size ? styles.selected : ''}`}
                              onClick={() => setSelectedSize(size)}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className={styles.colorSelector}>
                        <label>Boja:</label>
                        <div className={styles.options}>
                          {[...new Set(product.variants.map(v => v.color))].map(color => (
                            <button 
                              key={color}
                              className={`${styles.colorOption} ${selectedColor === color ? styles.selected : ''}`}
                              style={{ backgroundColor: color.toLowerCase() }}
                              onClick={() => setSelectedColor(color)}
                            >
                              <span className={styles.colorName}>{color}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className={styles.rightColumn}>
                  <div className={styles.productMeta}>
                    {product.show_vendor !== false && product.vendor && (
                      <p><strong>Proizvođač:</strong> {product.vendor}</p>
                    )}
                    <p><strong>Šifra:</strong> {product.sku}</p>
                    <p><strong>Kategorija:</strong> {(product as any).categories?.name || product.category || '-'}</p>
                  </div>
                </div>
              </div>
              </div>{/* end of modules+attributes flex wrapper */}

              <div className={styles.productActions}>
                <div className={styles.quantitySelector}>
                  <label>Količina:</label>
                  <div className={styles.quantityControls}>
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      type="button"
                      aria-label="Smanji količinu"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      min="1"
                      aria-label="Količina"
                    />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      type="button"
                      aria-label="Povećaj količinu"
                    >
                      +
                    </button>
                  </div>
                </div>
                
                <button 
                  onClick={handleAddToCart}
                  className={styles.addToCartButton}
                  disabled={!product?.stock_quantity}
                >
                  {product?.stock_quantity ? 'Dodaj u korpu' : 'Nema na stanju'}
                </button>
              </div>
            </div>
          </div>

          {(() => {
            const showAdditional = product.show_additional_information !== false;
            const showShipping = product.show_shipping_returns !== false;
            const effectiveTab =
              (activeTab === 'additional' && !showAdditional) ||
              (activeTab === 'shipping' && !showShipping)
                ? 'description'
                : activeTab;
            return (
              <div className={styles.productTabs}>
                <div className={styles.tabHeaders}>
                  <button
                    className={`${styles.tabButton} ${effectiveTab === 'description' ? styles.active : ''}`}
                    onClick={() => setActiveTab('description')}
                  >
                    Opis
                  </button>
                  {showAdditional && (
                    <button
                      className={`${styles.tabButton} ${effectiveTab === 'additional' ? styles.active : ''}`}
                      onClick={() => setActiveTab('additional')}
                    >
                      Dodatne informacije
                    </button>
                  )}
                  {showShipping && (
                    <button
                      className={`${styles.tabButton} ${effectiveTab === 'shipping' ? styles.active : ''}`}
                      onClick={() => setActiveTab('shipping')}
                    >
                      Dostava i povraćaji
                    </button>
                  )}
                </div>

                <div className={styles.tabContent}>
                  {effectiveTab === 'description' && (
                    <div id="description">
                      <ProductDescription description={product.description} />
                    </div>
                  )}
                  {effectiveTab === 'additional' && showAdditional && (
                    <div dangerouslySetInnerHTML={{ __html: sanitize(product.additional_information || '') }}></div>
                  )}
                  {effectiveTab === 'shipping' && showShipping && (
                    <div dangerouslySetInnerHTML={{ __html: sanitize(product.shipping_returns || '') }}></div>
                  )}
                </div>
              </div>
            );
          })()}

          {relatedProducts.length > 0 && (
            <div className={styles.relatedProducts}>
              <h2>Slični proizvodi</h2>
              <div className={styles.productsGrid}>
                {relatedProducts.map((relProd) => (
                  <Link
                    key={relProd.id}
                    to={`/p/${relProd.slug}`}
                    className={styles.productCard}
                  >
                    <img src={relProd.image_url} alt={relProd.name} width={220} height={220} loading="lazy" decoding="async" />
                    <h3>{relProd.name}</h3>
                    
                    <div className={styles.productCardPrices}>
                      <span className={styles.currentPrice}>
                        {formatPrice(relProd.price)}
                      </span>
                      
                      {relProd.old_price && (
                        <>
                          <span className={styles.oldPrice}>
                            {formatPrice(relProd.old_price)}
                          </span>
                          <span className={styles.discount}>
                            {Math.round(((relProd.old_price - relProd.price) / relProd.old_price) * 100)}% Popust
                          </span>
                        </>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {product && (
            <div className={styles.bundleSection}>
              <ProductDetails
                id={product.id}
                name={product.name}
                description={product.description}
                short_description={product.short_description}
                price={product.price}
                old_price={product.old_price}
              />
            </div>
          )}
          
          {/* Product Reviews Section - na kraju stranice iznad footera */}
          {product && product.id && (
            <ProductReviews productId={product.id} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductPage; 